import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import { useClientSideInfo } from "../checkout-modal/useClientSideInfo";
import userPurchaseRedirect from "@/utils/user/userPurchaseRedirect";
import { emitSuccessfulPurchaseEvent } from "./emitSuccessfulPurchaseEvent";
import { emitRedirectEvent } from "./emitRedirectEvent";
import { Brand, Offer } from "@prisma/client";
import Requires from "internal_components/Requires";

const PurchaseButton = ({
  shippingRequired,
  cardElement,
  brandAbbreviation,
  brandId,
  brandName,
  clientSideInfo,
  offers,
  stripe,
  validateForm,
  disabled,
}: {
  shippingRequired: boolean;
  cardElement: any;
  brandAbbreviation: Brand["abbreviation"];
  brandId: Brand["id"];
  brandName: Brand["name"];
  clientSideInfo: ReturnType<typeof useClientSideInfo>;
  offers: Array<Offer>;
  stripe: any;
  validateForm: () => Promise<boolean>;
  disabled: boolean;
}) => {
  const { getValues } = useFormContext();
  const name = getValues("name");
  const email = getValues("email");
  const address = getValues("address");
  const [loading, setLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | undefined>();
  const [successfulPurchase, setSuccessfulPurchase] = useState(false);
  const [isTest, setIsTest] = useState<boolean>();

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("test")?.toLowerCase() ===
        "true"
    ) {
      setIsTest(true);
    }
  }, []);

  const emitTestPurchaseEvent = () => {
    emitSuccessfulPurchaseEvent(
      "test_user_id",
      email,
      offers,
      clientSideInfo,
      buildPurchaseBody("test_payment_method")
    );
  };

  const buildPurchaseBody = (payment_method: string) => {
    return {
      ...clientSideInfo,
      hasAddress: shippingRequired,
      brandId: brandId,
      brandName: brandName,
      email: email,
      offer_ids: offers.map((offer) => offer.id),
      payment_method: payment_method,
      customerInfo: { ...address, name: address.name || name }, // TODO: need to pass validated if the address is validated
    };
  };

  const initiatePurchase = async () => {
    setPurchaseError(undefined);
    const formValidation = await validateForm();
    if (!formValidation) {
      setPurchaseError("Please fill out all required fields");
      return;
    }

    if (cardElement === undefined) {
      return;
    }

    setLoading(true);
    cardElement.update({ disabled: true });

    if (!stripe) {
      console.log("Error loading stripe, need to show error message");
      setLoading(false);
      cardElement.update({ disabled: false });
      return;
    }

    const setupIntentResponse = await fetch("/api/stripe/setupIntent/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        brandAbbreviation: brandAbbreviation,
      }),
    });

    const setupIntent = await setupIntentResponse.json();
    const setupIntentSecret = setupIntent.client_secret;

    const confirmation = await stripe.confirmCardSetup(setupIntentSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          email: email,
        },
      },
    });

    if (confirmation.error) {
      setLoading(false);
      cardElement.update({ disabled: false });
      setPurchaseError(confirmation.error.message);
      return;
    }

    const response = await fetch("/api/stripe/buy/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(
        buildPurchaseBody(confirmation.setupIntent.payment_method)
      ),
    });

    if (response.ok) {
      const responseJson = await response.json();
      emitSuccessfulPurchaseEvent(
        responseJson.user_id,
        responseJson.email,
        offers,
        clientSideInfo
      );
      setSuccessfulPurchase(true);

      const redirectUrl = await userPurchaseRedirect(
        email,
        brandId,
        responseJson.user
      );

      const modalIsInIframe = window.location !== window.parent.location;
      if (!modalIsInIframe && redirectUrl) {
        const protocol = window.location.protocol;
        const fullUrl = new URL(redirectUrl || "");
        fullUrl.protocol = protocol;
        window.location.replace(fullUrl);
      }

      setTimeout(() => {
        if (redirectUrl) {
          emitRedirectEvent(redirectUrl);
          // if not in an iframe, redirect to the url
        }
      }, 3000);
    } else {
      const json = await response.json();
      setPurchaseError(json.message || response.statusText);
    }

    setLoading(false);
    cardElement.update({ disabled: false });
  };

  if (isTest) {
    return (
      <button
        data-testid="test-purchase-button"
        onClick={emitTestPurchaseEvent}
      >
        Test
      </button>
    );
  }

  return (
    <div className="flex flex-col w-full items-center">
      {purchaseError && <div className="text-red-500">{purchaseError}</div>}

      <Requires value={!successfulPurchase}>
        <button
          data-testid="purchase-button"
          disabled={loading || disabled}
          onClick={initiatePurchase}
          type="submit"
          className={`${disabled ? "bg-red-200" : "bg-red-500"} transition-all duration-200 w-full md:w-1/2 rounded-lg py-3 mt-4 font-bold text-white`}
        >
          {loading ? "Processing..." : "Complete Order"}
        </button>
      </Requires>

      {successfulPurchase && (
        <div className="text-green-500">
          Purchase successful! You will be redirected momentarily.
        </div>
      )}
    </div>
  );
};

export default PurchaseButton;
